import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Lock, Search, Grid3x3, List, HardDrive, Users, Shield,
  ChevronRight, Star, Share2, X, Eye, Folder, Download,
  GraduationCap, Video, Instagram, Ghost, Send,
} from 'lucide-react'
import { trackEvent } from '../lib/tracking'

const DRIVE_INFO = {
  owner: 'Sushant Pradhan',
  ownerAvatar: 'SP',
  verified: true,
  title: 'Nepali Compiled Videos — Private Archive',
  description: 'Best compilations you\'ll never find anywhere else. Featuring top TikTok stars, college creators, and viral moments from across Nepal.',
  totalFiles: 3024,
  totalSize: '782.4 GB',
  views: '1.2M',
  followers: '48.7K',
  lastUpdated: '2 hours ago',
}

type FolderItem = {
  id: string
  name: string
  fileCount: number
  size: string
  lastModified: string
  icon: any
  gradient: string
  accentColor: string
  description: string
  trending?: boolean
}

const FOLDERS: FolderItem[] = [
  {
    id: 'nepali-college-girls',
    name: 'Nepali College Girls',
    fileCount: 847,
    size: '218.4 GB',
    lastModified: '2 hours ago',
    icon: GraduationCap,
    gradient: 'from-pink-500 via-rose-500 to-red-500',
    accentColor: 'pink',
    description: 'Collection from top Nepali colleges & universities',
    trending: true,
  },
  {
    id: 'nepali-tiktoker',
    name: 'Nepali Tiktoker',
    fileCount: 1243,
    size: '312.7 GB',
    lastModified: '5 hours ago',
    icon: Video,
    gradient: 'from-purple-500 via-fuchsia-500 to-pink-500',
    accentColor: 'purple',
    description: 'Top verified TikTok creators from Nepal',
    trending: true,
  },
  {
    id: 'instagram',
    name: 'Instagram',
    fileCount: 512,
    size: '128.9 GB',
    lastModified: '1 day ago',
    icon: Instagram,
    gradient: 'from-orange-500 via-pink-500 to-purple-600',
    accentColor: 'orange',
    description: 'Curated reels, stories & IGTV content',
  },
  {
    id: 'snapchat',
    name: 'Snapchat',
    fileCount: 298,
    size: '67.2 GB',
    lastModified: '3 days ago',
    icon: Ghost,
    gradient: 'from-yellow-400 via-yellow-500 to-amber-500',
    accentColor: 'yellow',
    description: 'Saved snaps & private story archives',
  },
  {
    id: 'telegram',
    name: 'Telegram',
    fileCount: 124,
    size: '55.2 GB',
    lastModified: '1 week ago',
    icon: Send,
    gradient: 'from-cyan-500 via-sky-500 to-blue-600',
    accentColor: 'cyan',
    description: 'Exclusive channel exports & group leaks',
  },
]

export default function SharedDrive() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [searchQuery, setSearchQuery] = useState('')
  const [showSignIn, setShowSignIn] = useState(false)
  const [signInContext, setSignInContext] = useState<{ folder?: FolderItem; action: string }>({ action: 'access' })
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  useEffect(() => {
    trackEvent('page_view', {
      page: 'nepali_compiled_videos',
      drive_title: DRIVE_INFO.title,
    })
  }, [])

  const filteredFolders = FOLDERS.filter((f) =>
    f.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleFolderClick = (folder: FolderItem, action: 'open' | 'download' = 'open') => {
    trackEvent('folder_click', {
      folder_id: folder.id,
      folder_name: folder.name,
      action,
      file_count: folder.fileCount,
      size: folder.size,
    })
    setSignInContext({ folder, action })
    setShowSignIn(true)
  }

  const handleDownloadAll = () => {
    trackEvent('download_all_click', {
      total_files: DRIVE_INFO.totalFiles,
      total_size: DRIVE_INFO.totalSize,
    })
    setSignInContext({ action: 'download_all' })
    setShowSignIn(true)
  }

  return (
    <div className="min-h-screen bg-void-black">
      {/* Animated gradient background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-nebula-purple/20 blur-3xl animate-pulse" />
        <div className="absolute top-1/2 -right-40 h-96 w-96 rounded-full bg-cosmic-blue/20 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-0 left-1/3 h-96 w-96 rounded-full bg-aurora-cyan/10 blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Top Navigation */}
      <nav className="sticky top-0 z-40 border-b border-white/5 backdrop-blur-xl bg-void-black/80">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4">
          <a href="/" className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-cosmic">
              <HardDrive className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold gradient-text">Cloudsize</span>
          </a>

          <div className="hidden md:flex flex-1 max-w-xl mx-8">
            <div className="relative w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-star-white/40" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search folders..."
                className="w-full rounded-xl border border-white/10 bg-white/5 px-12 py-2.5 text-sm text-star-white placeholder:text-star-white/40 focus:border-nebula-purple/50 focus:outline-none focus:ring-2 focus:ring-nebula-purple/20"
              />
              <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden rounded bg-white/10 px-2 py-0.5 text-xs text-star-white/50 md:inline">⌘K</kbd>
            </div>
          </div>

          <button
            onClick={() => { setSignInContext({ action: 'nav_signin' }); setShowSignIn(true) }}
            className="glass-button rounded-xl px-5 py-2.5 text-sm font-semibold"
          >
            Sign In
          </button>
        </div>
      </nav>

      {/* Drive Header */}
      <section className="relative border-b border-white/5">
        <div className="mx-auto max-w-[1600px] px-6 py-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between"
          >
            <div className="flex-1">
              <div className="mb-3 flex items-center gap-2 text-sm text-star-white/50">
                <Folder className="h-4 w-4" />
                <span>Public Drive</span>
                <ChevronRight className="h-3 w-3" />
                <span className="text-star-white/70">Nepali Compiled Videos</span>
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
                  Live
                </span>
              </div>

              <h1 className="mb-3 text-4xl font-bold text-star-white md:text-5xl">
                {DRIVE_INFO.title}
              </h1>

              <p className="mb-6 max-w-2xl text-lg text-star-white/70">
                {DRIVE_INFO.description}
              </p>

              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-cosmic text-sm font-bold text-white">
                  {DRIVE_INFO.ownerAvatar}
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-star-white">{DRIVE_INFO.owner}</span>
                    {DRIVE_INFO.verified && (
                      <svg className="h-4 w-4 text-cosmic-blue" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l2.4 4.8 5.3.8-3.8 3.7.9 5.3L12 14l-4.8 2.6.9-5.3L4.3 7.6l5.3-.8z" />
                      </svg>
                    )}
                  </div>
                  <div className="text-xs text-star-white/50">Updated {DRIVE_INFO.lastUpdated}</div>
                </div>
                <button className="ml-4 rounded-lg border border-nebula-purple/30 bg-nebula-purple/10 px-4 py-1.5 text-sm font-medium text-nebula-purple hover:bg-nebula-purple/20 transition">
                  + Follow
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                <StatBadge icon={Folder} label="Folders" value={FOLDERS.length.toString()} />
                <StatBadge icon={Video} label="Files" value={DRIVE_INFO.totalFiles.toLocaleString()} />
                <StatBadge icon={HardDrive} label="Size" value={DRIVE_INFO.totalSize} />
                <StatBadge icon={Eye} label="Views" value={DRIVE_INFO.views} />
                <StatBadge icon={Users} label="Followers" value={DRIVE_INFO.followers} />
              </div>
            </div>

            <div className="flex flex-col gap-3 lg:min-w-[280px]">
              <button
                onClick={handleDownloadAll}
                className="flex items-center justify-center gap-2 rounded-xl bg-gradient-cosmic px-6 py-3.5 font-semibold text-white shadow-lg shadow-nebula-purple/30 hover:shadow-nebula-purple/50 transition-all hover:scale-[1.02]"
              >
                <Download className="h-4 w-4" />
                Download All (782 GB)
              </button>
              <button
                onClick={() => { trackEvent('share_drive_click'); setSignInContext({ action: 'share' }); setShowSignIn(true) }}
                className="glass-button flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold"
              >
                <Share2 className="h-4 w-4" />
                Share Drive
              </button>
              <button
                onClick={() => { trackEvent('save_library_click'); setSignInContext({ action: 'save' }); setShowSignIn(true) }}
                className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-6 py-3 font-medium text-star-white/70 hover:bg-white/10 transition"
              >
                <Star className="h-4 w-4" />
                Save to My Library
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="mx-auto max-w-[1600px] px-6 py-8">
        <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
          {/* Sidebar */}
          <aside className="space-y-3">
            <div className="rounded-2xl border border-nebula-purple/20 bg-gradient-to-br from-nebula-purple/10 to-cosmic-blue/5 p-5">
              <Shield className="mb-3 h-6 w-6 text-nebula-purple" />
              <h4 className="mb-1 font-semibold text-star-white">DMCA Protected</h4>
              <p className="text-xs text-star-white/60 leading-relaxed">
                All access is logged for copyright compliance. Your IP, location, and device are being tracked.
              </p>
            </div>

            <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5">
              <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-star-white/40">
                Quick Info
              </h4>
              <div className="space-y-3 text-sm">
                <InfoRow label="Total Folders" value={FOLDERS.length.toString()} />
                <InfoRow label="Total Files" value={DRIVE_INFO.totalFiles.toLocaleString()} />
                <InfoRow label="Total Size" value={DRIVE_INFO.totalSize} />
                <InfoRow label="Privacy" value="Private" />
              </div>
            </div>
          </aside>

          {/* Folders */}
          <main>
            <div className="mb-6 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-star-white">Folders</h2>
                <span className="text-sm text-star-white/50">
                  {filteredFolders.length} items
                </span>
              </div>

              <div className="flex rounded-lg border border-white/10 bg-white/5 p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`rounded-md p-1.5 transition ${viewMode === 'grid' ? 'bg-white/10 text-star-white' : 'text-star-white/50'}`}
                >
                  <Grid3x3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`rounded-md p-1.5 transition ${viewMode === 'list' ? 'bg-white/10 text-star-white' : 'text-star-white/50'}`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>

            {viewMode === 'grid' ? (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {filteredFolders.map((folder, idx) => {
                  const Icon = folder.icon
                  return (
                    <motion.div
                      key={folder.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.08, duration: 0.5 }}
                      onMouseEnter={() => setHoveredId(folder.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      onClick={() => handleFolderClick(folder, 'open')}
                      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02] backdrop-blur-sm transition-all hover:border-nebula-purple/40 hover:bg-white/[0.05] hover:shadow-2xl hover:shadow-nebula-purple/20 hover:-translate-y-1"
                    >
                      {/* Gradient top bar */}
                      <div className={`h-1.5 bg-gradient-to-r ${folder.gradient}`} />

                      <div className="p-6">
                        {/* Icon + Trending */}
                        <div className="mb-5 flex items-start justify-between">
                          <div className={`relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${folder.gradient} shadow-lg`}>
                            <Icon className="h-8 w-8 text-white" />
                            <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${folder.gradient} opacity-0 blur-xl transition group-hover:opacity-60`} />
                          </div>
                          {folder.trending && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2.5 py-1 text-xs font-bold text-red-400 border border-red-500/20">
                              🔥 HOT
                            </span>
                          )}
                        </div>

                        {/* Name + Description */}
                        <h3 className="mb-1.5 text-xl font-bold text-star-white">
                          {folder.name}
                        </h3>
                        <p className="mb-5 text-sm text-star-white/60 line-clamp-2 min-h-[2.5rem]">
                          {folder.description}
                        </p>

                        {/* Stats */}
                        <div className="flex items-center justify-between border-t border-white/5 pt-4">
                          <div className="flex flex-col">
                            <span className="text-xs text-star-white/40">Files</span>
                            <span className="text-sm font-semibold text-star-white">
                              {folder.fileCount.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs text-star-white/40">Size</span>
                            <span className="text-sm font-semibold text-star-white">{folder.size}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs text-star-white/40">Updated</span>
                            <span className="text-sm font-semibold text-star-white">{folder.lastModified}</span>
                          </div>
                        </div>
                      </div>

                      {/* Hover Overlay */}
                      <AnimatePresence>
                        {hoveredId === folder.id && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 flex items-center justify-center bg-void-black/70 backdrop-blur-sm"
                          >
                            <div className="flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 px-5 py-3 text-sm font-semibold text-white">
                              <Lock className="h-4 w-4" />
                              Sign in to Open Folder
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )
                })}
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02] backdrop-blur-sm">
                <div className="hidden border-b border-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-star-white/40 md:grid md:grid-cols-[1fr_120px_120px_140px_120px] md:gap-4">
                  <div>Name</div>
                  <div>Files</div>
                  <div>Size</div>
                  <div>Last Modified</div>
                  <div className="text-right">Actions</div>
                </div>
                {filteredFolders.map((folder, idx) => {
                  const Icon = folder.icon
                  return (
                    <motion.div
                      key={folder.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      onClick={() => handleFolderClick(folder, 'open')}
                      className="group grid cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-4 border-b border-white/5 px-4 py-4 last:border-0 hover:bg-white/[0.04] transition md:grid-cols-[1fr_120px_120px_140px_120px]"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${folder.gradient} shadow-md`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-semibold text-star-white">
                              {folder.name}
                            </span>
                            {folder.trending && (
                              <span className="text-xs">🔥</span>
                            )}
                          </div>
                          <div className="truncate text-xs text-star-white/50">
                            {folder.description}
                          </div>
                        </div>
                      </div>
                      <div className="hidden text-sm text-star-white/70 md:block">
                        {folder.fileCount.toLocaleString()}
                      </div>
                      <div className="hidden text-sm text-star-white/70 md:block">
                        {folder.size}
                      </div>
                      <div className="hidden text-sm text-star-white/60 md:block">
                        {folder.lastModified}
                      </div>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleFolderClick(folder, 'download') }}
                          className="rounded-md p-2 hover:bg-white/10 transition"
                          title="Download"
                        >
                          <Download className="h-4 w-4 text-star-white/70" />
                        </button>
                        <Lock className="h-4 w-4 text-nebula-purple/70 ml-1" />
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            )}

            {/* Locked notice */}
            <div className="mt-8 rounded-2xl border border-white/5 bg-gradient-to-r from-nebula-purple/5 via-cosmic-blue/5 to-aurora-cyan/5 p-8 text-center">
              <Lock className="mx-auto mb-3 h-8 w-8 text-nebula-purple" />
              <h3 className="mb-2 text-lg font-bold text-star-white">
                All {DRIVE_INFO.totalFiles.toLocaleString()} files are locked
              </h3>
              <p className="mb-4 text-sm text-star-white/60">
                Sign in with Google to unlock all 5 folders and access the complete library.
              </p>
              <button
                onClick={() => { setSignInContext({ action: 'unlock_all' }); setShowSignIn(true) }}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-cosmic px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-nebula-purple/30 hover:scale-105 transition"
              >
                Unlock Full Library
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </main>
        </div>
      </div>

      {/* Sign In Modal */}
      <AnimatePresence>
        {showSignIn && (
          <SignInModal
            onClose={() => setShowSignIn(false)}
            context={signInContext}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

function StatBadge({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-white/5 bg-white/[0.03] px-4 py-2">
      <Icon className="h-4 w-4 text-nebula-purple" />
      <div>
        <div className="text-xs text-star-white/50">{label}</div>
        <div className="text-sm font-bold text-star-white">{value}</div>
      </div>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-star-white/50">{label}</span>
      <span className="font-semibold text-star-white">{value}</span>
    </div>
  )
}

function SignInModal({
  onClose,
  context,
}: {
  onClose: () => void
  context: { folder?: FolderItem; action: string }
}) {
  useEffect(() => {
    trackEvent('signin_modal_shown', {
      action: context.action,
      folder_name: context.folder?.name,
      folder_id: context.folder?.id,
    })
  }, [context])

  const handleGoogleSignIn = () => {
    trackEvent('google_signin_click', {
      action: context.action,
      folder_name: context.folder?.name,
    })
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
    const params = new URLSearchParams({
      return_to: window.location.pathname,
      session_id: localStorage.getItem('cs_session_id') || '',
      visitor_id: localStorage.getItem('cs_visitor_id') || '',
    })
    window.location.href = `${apiUrl}/api/v1/auth/google/start?${params.toString()}`
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-void-black/80 backdrop-blur-md p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: 'spring', duration: 0.4 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-void-black via-void-black to-nebula-purple/20 p-8 shadow-2xl"
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 text-star-white/60 hover:bg-white/10 hover:text-star-white transition"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-nebula-purple/30 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-cosmic-blue/30 blur-3xl" />

        <div className="relative">
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-cosmic shadow-lg shadow-nebula-purple/50">
            <Lock className="h-8 w-8 text-white" />
          </div>

          <h2 className="mb-2 text-2xl font-bold text-star-white">
            {context.folder ? `Unlock "${context.folder.name}"` : 'Sign in to Continue'}
          </h2>
          <p className="mb-6 text-star-white/60">
            {context.folder
              ? `Access ${context.folder.fileCount.toLocaleString()} files (${context.folder.size}) in this folder.`
              : 'Unlock all 5 folders with 3,024 videos (782 GB) — free and instant.'}
          </p>

          <div className="mb-6 space-y-2">
            <Benefit text="Stream videos in HD/4K quality" />
            <Benefit text="Download originals — no compression" />
            <Benefit text="Save folders to your personal library" />
            <Benefit text="Get notified of new additions" />
          </div>

          <button
            onClick={handleGoogleSignIn}
            className="mb-3 flex w-full items-center justify-center gap-3 rounded-xl bg-white px-6 py-3.5 font-semibold text-void-black hover:bg-white/90 transition hover:scale-[1.02]"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </button>

          <button className="w-full rounded-xl border border-white/10 bg-white/5 px-6 py-3 text-sm font-medium text-star-white/70 hover:bg-white/10 transition">
            Continue with Email
          </button>

          <p className="mt-6 text-center text-xs text-star-white/40">
            By continuing, you agree to our Terms & acknowledge DMCA tracking for copyright protection.
          </p>
        </div>
      </motion.div>
    </motion.div>
  )
}

function Benefit({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-star-white/80">
      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-nebula-purple/20">
        <svg className="h-3 w-3 text-nebula-purple" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      {text}
    </div>
  )
}
